import { RecallStatusTypeLookup } from "@/Common/Components/CallCenter/CallCenter.types";
import { CreatePatientCallCommunicationRequest } from "@/Common/Components/CallCenter/PatientCallCommunication.types";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { EnumAppointmentSource } from "@/Common/Models/Appointments/EnumAppointmentSource";
import { CallCenterCallDirection } from "@/Common/Models/CallCenter/CallCenterCallDirection";
import { PortalLinkedItemType } from "@/Common/Models/Portal/Enums/PortalLinkedItemType";
import { SharedItemMessage } from "@/Common/Models/Portal/SharedItem/SharedItemMessage";
import { PrivateBill_BilledTo } from "@/Common/Models/UI/Enums";
import * as ServerOperations from "@/Common/Operations/operations.server";
import * as SubServerOperations from "@/Common/Operations/operations.server.sub";
import { BaseAction, IRequestExecutionContext, IResponseExecutionContext } from "@/Common/Stores/Actions/BaseAction";
import { IBillableService } from "@/Patient/Models/UI/History/IBillableService";
import { IPatientCommunicationsModel } from "@/Patient/Models/UI/History/IPatientCommunicationsModel";
import { IPatientPrivateBillModel } from "@/Patient/Models/UI/History/IPatientPrivateBillModel";

class PatientHistoryActions extends BaseAction {
    public static PatientCallCommunicationHistoryManualCallIdAndProvider(): string {
        return "Manual";
    }

    constructor(onBeforeExecute?: IRequestExecutionContext, onBeforeHandleResponse?: IResponseExecutionContext) {
        super(onBeforeExecute, onBeforeHandleResponse);
    }

    public LoadCostCenterForMexico(siteId: string): void {
        var requestData = {
            SiteId: ""
        };

        requestData.SiteId = siteId;

        this.Execute(ServerOperations.Server.Myle_Patient_History_PrivateBill_LoadCostCenterForMexico, requestData);
    }

    public PrintPrivateBill(privateBillId: string): void {
        var requestData = {
            PrivateBillId: privateBillId
        };

        this.Execute(ServerOperations.Server.Myle_Patient_History_GeneratePatientPrivateBillPDF, requestData);
    }

    public SavePrivateBill(privateBillModel: IPatientPrivateBillModel, billedServices: IBillableService[], patientId: string, createdById: string, isFromClose: boolean, isInPortalSyncWorkflow: boolean = false): void {
        let provider = privateBillModel.get("SelectedClinicians")?.toArray();
        let discountType = privateBillModel.get("IsCashSelected") === true ? 0 : 1;

        let fromEditProviderId: string;
        if (provider === null)
            fromEditProviderId = cacheStore.EmployeeCache.filter(c => (cacheStore.GetLocalizedValueByLookupId(c.PrefixLookupId) + " " + c.LastName + ", " + c.FirstName) === privateBillModel.get("Provider"))?.[0]?.ContactFacet_id;

        let requestData = {
            DateOfVisit: MyleHelper.GetStandardizedDateString(privateBillModel.get("SelectedDateOfVisit")),
            SiteId: privateBillModel.get("SelectedSiteId"),
            ProviderId: provider?.[0] ? provider[0].ContactFacetId : fromEditProviderId,
            CreatedById: createdById,
            PatientId: patientId,
            PaymentMethod: Number(privateBillModel.get("SelectedPaymentMethod")),
            DiscountType: discountType,
            GSTPercent: privateBillModel.get("GSTPercent"),
            QSTPercent: privateBillModel.get("QSTPercent"),
            HSTPercent: privateBillModel.get("HSTPercent"),
            GenericTaxPercent: privateBillModel.get("GenericTaxPercent"),
            Discount: privateBillModel.get("NewBillDiscount"),
            TotalGST: privateBillModel.get("NewBillGSTTotal"),
            TotalQST: privateBillModel.get("NewBillQSTTotal"),
            TotalHST: privateBillModel.get("NewBillHSTTotal"),
            TotalGenericTax: privateBillModel.get("NewBillGenericTaxTotal"),
            Total: privateBillModel.get("NewBillTotal"),
            Notes: privateBillModel.get("NewBillNotes"),
            BilledServices: [],
            IsFromClose: isFromClose,
            IsFromEdition: privateBillModel.get("IsEditBill"),
            PrivateBillId: privateBillModel.get("SelectedPrivateBillId"),
            RecipientName: null,
            RecipientAddress: null,
            RecipientPhoneNumber: null,
            RecipientFaxNumber: null,
            BilledTo: Number(PrivateBill_BilledTo.Patient)
        };

        if (cacheStore.ClinicSettings.EnablePrivateBillRecipient) {
            const recipientDetails = privateBillModel.get("SelectedBillRecipientDetails");

            requestData.RecipientName = recipientDetails.RecipientName;
            requestData.RecipientAddress = recipientDetails.RecipientAddress;
            requestData.RecipientPhoneNumber = recipientDetails.RecipientPhoneNumber;
            requestData.RecipientFaxNumber = recipientDetails.RecipientFaxNumber;
            requestData.BilledTo = privateBillModel.get("SelectedBillBilledTo");
        }

        for (let service of billedServices) {
            if (service.get("SelectedService") == null)
                continue;

            let billData = {
                Qty: service.get("Qty"),
                Price: service.get("Price"),
                ServiceNumber: service.get("ServiceNumber"),
                NameFr: service.get("NameFr"),
                NameEn: service.get("NameEn"),
                NameEs: service.get("NameEs"),
                GST: service.get("GST"),
                QST: service.get("QST"),
                HST: service.get("HST"),
                GenericTax: service.get("GenericTax"),
                HadGST: service.get("HasGST") == true ? true : false,
                HadQST: service.get("HasQST") == true ? true : false,
                HadHST: service.get("HasHST") == true ? true : false,
                HadGenericTax: service.get("HasGenericTax") == true ? true : false,
                CostCenterId: service.get("SelectedService").Service.CostCenterId
            };

            requestData.BilledServices.push(billData);
        }

        if (!isInPortalSyncWorkflow)
            this.Execute(ServerOperations.Server.Myle_Patient_History_SavePrivateBill, requestData, SubServerOperations.PrivateBill.Save);
        else
            this.Execute(ServerOperations.Server.Myle_Patient_History_SavePrivateBill, requestData, SubServerOperations.PrivateBill.SaveWithPortalSyncWorkFlow);
    }

    public InvalidatePrivateBill(privateBillId: string, invalidationNote: string): void {
        var requestData = {
            PrivateBillId: privateBillId,
            InvalidationNote: invalidationNote,
            EmployeeId: sessionStore.getCurrentEmployee().ContactFacetId
        };

        this.Execute(ServerOperations.Server.Myle_Patient_History_InvalidatePrivateBill, requestData);
    }

    public LoadPrivateBillInfo(billId: string): void {
        var requestData = {
            PrivateBillId: billId
        };

        this.Execute(ServerOperations.Server.Myle_Patient_History_GetSelectedBillInformation, requestData, SubServerOperations.PrivateBill.LoadInfoFromHistory);
    }

    public LoadPatientPrivateBill(patientId: string, searchText: string, dateFrom: Date, dateTo: Date): void {
        var requestData = {
            PatientId: patientId,
            SearchText: searchText,
            DateFrom: dateFrom ? MyleHelper.GetStandardizedDateString(dateFrom) : null,
            DateTo: dateTo ? MyleHelper.GetStandardizedDateString(dateTo) : null
        };

        this.Execute(ServerOperations.Server.Myle_Patient_History_GetPatientPrivateBill, requestData);
    }

    public SaveModificationAppointment(appointmentId: string, stateLookupId: string, stateName: string, note: string, isPaid: boolean, stateCode: string, subject: string, notifyPatient: boolean, isTeleconsultation: boolean): void {
        var requestData = {
            AppointmentId: appointmentId,
            StateLookupId: stateLookupId,
            StateName: stateName,
            StateCode: stateCode,
            Note: note,
            IsPaid: isPaid,
            IsTeleconsultation: isTeleconsultation,
            Subject: subject,
            Source: EnumAppointmentSource.Myle_PatientProfile,
            SkipPatientBookingReminder: !notifyPatient
        };

        this.Execute(ServerOperations.Server.Myle_Appointment_ChangeStatus, requestData);
    }

    public PrintAppointmentLabel(patientNameString: string, employeeNameString: string, appointmentDate: Date, clinicNameString: string, clinicAddressString: string, clinicPhoneNumberString: string): void {
        var requestData = {

            AppointmentDate: appointmentDate,
            PatientNameString: patientNameString,
            EmployeeNameString: employeeNameString,
            ClinicNameString: clinicNameString,
            ClinicAddressString: clinicAddressString,
            ClinicPhoneNumberString: clinicPhoneNumberString
        };

        this.Execute(ServerOperations.Server.Myle_Calendar_GenerateAppointmentConfirmationPDF, requestData);
    }

    public LoadPatientAppointment(patientId: string, showOnlyCurrent: boolean, searchText: string, fromDate: Date, toDate: Date, selectedSiteId: string): void {
        var requestData = {
            PatientId: patientId,
            ShowOnlyCurrent: showOnlyCurrent,
            SearchText: searchText,
            FromDate: fromDate ? fromDate : null,
            ToDate: toDate ? toDate : null,
            SiteId: selectedSiteId
        };

        this.Execute(ServerOperations.Server.Myle_Patient_History_GetAppointment, requestData);
    }

    public PrintPatientAppointmentHistory(patientId: string, showOnlyCurrent: boolean, searchText: string, fromDate: Date, toDate: Date, selectedSiteId: string): void {
        var requestData = {
            PatientId: patientId,
            ShowOnlyCurrent: showOnlyCurrent,
            SearchText: searchText,
            FromDate: fromDate,
            ToDate: toDate,
            SiteId: selectedSiteId
        };

        this.Execute(ServerOperations.Server.Myle_Patient_History_GeneratePatientAppointmentHistoryPDF, requestData);
    }

    public MarkSelectedRecallAsCompleted(recallId: string): void {
        var requestData = {
            RecallId: recallId
        };

        this.Execute(ServerOperations.Server.Myle_Patient_History_MarkSelectedRecallAsCompleted, requestData);
    }

    public LoadRecallCommunication(recallId: string): void {
        var requestData = {
            RecallId: recallId
        };

        this.Execute(ServerOperations.Server.Myle_Patient_History_GetRecallCommunication, requestData);
    }

    public LoadPatientRecall(patientId: string, searchText: string, showOnlyNotCompleted: boolean, fromDate: Date, toDate: Date): void {
        var requestData = {
            PatientId: patientId,
            SearchText: searchText,
            ShowOnlyNotCompleted: showOnlyNotCompleted,
            FromDate: fromDate ? fromDate : null,
            ToDate: toDate ? toDate : null
        };

        this.Execute(ServerOperations.Server.Myle_Patient_History_GetRecalls, requestData);
    }

    public CreateNewCommunication(patientId: string, model: IPatientCommunicationsModel): void {
        var requestData = {
            PatientId: patientId,
            Note: model.get("Note"),
            EmployeeId: cacheStore.GetCurrentEmployee().ContactFacet_id,
            FollowUpLookupId: model.get("SelectedStatusId"),
            Version: 1
        };
        this.Execute(ServerOperations.Server.Myle_Patient_History_CreateCommunication, requestData);
    }

    public CreateNewCallCommunication(patientId: string, model: IPatientCommunicationsModel): void {
        const requestData: CreatePatientCallCommunicationRequest = {
            PatientId: GuidHelper.AsGuid(patientId),
            CallId: PatientHistoryActions.PatientCallCommunicationHistoryManualCallIdAndProvider(),
            ProviderId: PatientHistoryActions.PatientCallCommunicationHistoryManualCallIdAndProvider(),
            Note: model.get("Note"),
            CallReasons: model.get("CallReasons")?.toArray(),
            CallDirection: cacheStore.GetCodeByLookupId(model.get("SelectedStatusId")) == RecallStatusTypeLookup.OutgoingCall
                ? CallCenterCallDirection.Outbound
                : CallCenterCallDirection.Inbound,
            EmployeeId: model.get("SelectedClinician")?.ContactFacetId,
            CallStart: model.get("CallStartDateTime"),
            CallEnd: model.get("CallEndTime"),
            FollowUpLookupId: GuidHelper.AsGuid(model.get("SelectedStatusId")),
            Version: 1
        };
        this.Execute(ServerOperations.Server.Myle_CallCenter_CallCommunicationAdd, requestData);
    }

    public DeleteCallCommunicationRecord(model: IPatientCommunicationsModel): void {
        const requestData = {
            PatientCommunicationHistoryId: model.get("PatientCommunicationHistoryId")
        };
        this.Execute(ServerOperations.Server.Myle_CallCenter_CallCommunicationDelete, requestData);
    }

    public EditCallCommunication(model: IPatientCommunicationsModel): void {
        const requestData = {
            PatientCommunicationHistoryId: model.get("PatientCommunicationHistoryId"),
            Note: model.get("Note"),
            CallReasons: model.get("CallReasons")
        };

        this.Execute(ServerOperations.Server.Myle_CallCenter_CallCommunicationUpdate, requestData);
    }

    public LoadPatientCommunications(patientId: string): void {
        var requestData = {
            PatientId: patientId
        };

        this.Execute(ServerOperations.Server.Myle_Patient_History_GetCommunications, requestData);
    }

    public LoadPatientMessage(patientId: string, searchText: string, fromDate: Date, toDate: Date, unreadOnly: boolean = false): void {
        var requestData = {
            PatientId: patientId,
            SearchText: searchText,
            FromDate: fromDate ? fromDate : null,
            ToDate: toDate ? toDate : null,
            UnreadOnly: unreadOnly
        };
        this.Execute(ServerOperations.Server.Myle_Patient_History_GetMessage, requestData);
    }

    public LoadMessageOrTaskNotes(messageOrTaskId: string, subOp: number): void {
        var requestData = {
            MessageOrTaskId: messageOrTaskId
        };

        this.Execute(ServerOperations.Server.Myle_Patient_History_LoadMessageOrTaskNote, requestData, subOp);
    }

    public LoadPatientTask(patientId: string, searchText: string, fromDate: Date, toDate: Date): void {
        var requestData = {
            PatientId: patientId,
            SearchText: searchText,
            FromDate: fromDate ? fromDate : null,
            ToDate: toDate ? toDate : null
        };
        this.Execute(ServerOperations.Server.Myle_Patient_History_GetTasks, requestData);
    }

    public LoadPortalSharedItem(sharedItemId: string, sharedItemType: PortalLinkedItemType): void {
        var requestData =
            {
                SharedItemId: sharedItemId
            };
        if (sharedItemType == PortalLinkedItemType.PrivateBill)
            this.Execute(ServerOperations.Server.Myle_Patient_Portal_GetSharedItem, requestData, SubServerOperations.PortalLinkedItemStatus.PrivateBill);
        else
            this.Execute(ServerOperations.Server.Myle_Patient_Portal_GetSharedItem, requestData, SubServerOperations.PortalLinkedItemStatus.Communication);
    }

    public sendHistorySharedItemToPortal(
        objectId: string,
        sharedItemId: string,
        subject: string,
        sharedItemType: PortalLinkedItemType,
        sentByContactId: string,
        patientContactId: string,
        createdByUserId: string,
        portalMessages: SharedItemMessage[],
        includeLinkedItem: boolean,
        linkedItemUpdated: boolean,
        canPatientReplyWithMessage: boolean
    ): void {
        let requestData = {
            ObjectId: objectId,
            SharedItemType: sharedItemType,
            Subject: subject,
            EmployeeContactId: sentByContactId,
            PatientContactId: patientContactId,
            CreatedByUserId: createdByUserId,
            IncludeLinkedItem: includeLinkedItem,
            Messages: portalMessages,
            SharedItemId: sharedItemId,
            LabMessageDTO: null,
            LinkedItemUpdated: linkedItemUpdated,
            CanPatientReplyWithMessage: canPatientReplyWithMessage
        };
        if (sharedItemType == PortalLinkedItemType.CommunicationHistory)
            this.Execute(ServerOperations.Server.Myle_Patient_Portal_SyncSharedItem, requestData, SubServerOperations.PortalLinkedItemStatus.Communication);
        else
            this.Execute(ServerOperations.Server.Myle_Patient_Portal_SyncSharedItem, requestData, SubServerOperations.PortalLinkedItemStatus.PrivateBill);
    }

    public revokeHistorySharedItem(sharedItemId: string, sharedItemMessages: number[], userId: string, sharedItemType: PortalLinkedItemType, billId: string, includeLinkedItem: boolean): void {
        let requestData =
        {
            SharedItemId: sharedItemId,
            SharedItemMessageIds: sharedItemMessages,
            UserId: userId,
            IncludeLinkedItem: includeLinkedItem,
            SharedItemType: sharedItemType,
            ObjectId: billId
        };
        if (sharedItemType == PortalLinkedItemType.CommunicationHistory)
            this.Execute(ServerOperations.Server.Myle_Patient_Portal_RevokeSharedItem, requestData, SubServerOperations.PortalLinkedItemStatus.Communication);
        else
            this.Execute(ServerOperations.Server.Myle_Patient_Portal_RevokeSharedItem, requestData, SubServerOperations.PortalLinkedItemStatus.PrivateBill);
    }

    public DeleteSelectedBill(privateBillId: string): void {
        var requestData = {
            PrivateBillId: privateBillId
        };

        this.Execute(ServerOperations.Server.Myle_Patient_History_DeleteSelectedBill, requestData);
    }

    public CloseSelectedBill(privateBillId: string): void {
        var requestData = {
            PrivateBillId: privateBillId
        };

        this.Execute(ServerOperations.Server.Myle_Patient_History_CloseSelectedBill, requestData);
    }

    public CloseAndPrintSelectedBill(privateBillId: string): void {
        var requestData = {
            PrivateBillId: privateBillId
        };

        this.Execute(ServerOperations.Server.Myle_Patient_History_CloseSelectedBill, requestData);
    }

    public SetTaskStatus(individualMessageId: string, statusId: string): void {
        var requestData = {
            IndividualMessageId: individualMessageId,
            CurrentUserId: sessionStore.getCurrentEmployee().ContactId,
            TaskStatusLookupId: statusId
        };

        this.Execute(ServerOperations.Server.Myle_SetTaskStatus, requestData);
    }

    public loadSingleTask(taskId: string): void {
        this.Execute(
            ServerOperations.Server.Myle_Patient_History_SingleTask,
            { TaskId: taskId }
        );
    }

    public loadSingleMessage(messageId: string): void {
        this.Execute(
            ServerOperations.Server.Myle_Patient_History_SingleMessage,
            { IndividualMessageId: messageId }
        );
    }

    public loadSingleRecall(recallId: string): void {
        this.Execute(
            ServerOperations.Server.Myle_Patient_History_SingleRecall,
            { RecallId: recallId }
        );
    }

    public printAppointmentCertificate(appointmentDate: Date, siteId: string, patientContactId: string): void {
        const requestData = {
            AppointmentDate: appointmentDate,
            SiteId: siteId,
            PatientContactId: patientContactId
        };

        this.Execute(ServerOperations.Server.Myle_AppointmentCertificate_GeneratePdf, requestData);
    }
}

export { PatientHistoryActions };
